/**
 * Iconos de linea, dibujados a mano en SVG.
 *
 * Se reemplazan los emoji que habia antes: cada sistema operativo los dibuja a
 * su manera —y en color— asi que la barra lateral se veia distinta en cada
 * equipo y no seguia el tema. Estos heredan `currentColor` y pesan unos pocos
 * cientos de bytes, bastante menos que una fuente de iconos.
 */

export type NombreIcono =
    | 'tienda'
    | 'vender'
    | 'caja'
    | 'productos'
    | 'inventario'
    | 'devoluciones'
    | 'clientes'
    | 'reportes'
    | 'promociones'
    | 'usuarios'
    | 'configuracion'
    | 'sol'
    | 'luna'
    | 'salir'
    | 'menu'
    | 'plegar'
    | 'desplegar'

const TRAZOS: Record<NombreIcono, React.ReactNode> = {
    tienda: (
        <>
            <path d="M3 9h18l-1.5-5.5A1 1 0 0 0 18.5 3h-13a1 1 0 0 0-.97.75L3 9Z" />
            <path d="M4.5 9v10a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V9" />
            <path d="M9.5 20v-5.5h5V20" />
        </>
    ),
    vender: (
        <>
            <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
            <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
            <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
        </>
    ),
    caja: (
        <>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.5" />
            <path d="M5.5 12h.01M18.5 12h.01" />
        </>
    ),
    productos: (
        <>
            <path d="M21 8.5v7l-9 5-9-5v-7l9-5 9 5Z" />
            <path d="m3 8.5 9 5 9-5" />
            <path d="M12 13.5v7" />
        </>
    ),
    inventario: (
        <>
            <rect x="8" y="2.5" width="8" height="4" rx="1.25" />
            <path d="M16 4.5h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h2" />
            <path d="M8 11h8M8 15.5h5" />
        </>
    ),
    devoluciones: (
        <>
            <path d="M3 12a9 9 0 1 0 2.64-6.36" />
            <path d="M3 3.5v5h5" />
        </>
    ),
    clientes: (
        <>
            <circle cx="9" cy="7.5" r="4" />
            <path d="M2 20.5v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" />
            <path d="M17 4a4 4 0 0 1 0 7M18 14.7a5 5 0 0 1 4 4.8v1" />
        </>
    ),
    reportes: (
        <>
            <path d="M3 20.5h18" />
            <rect x="4.5" y="11" width="4" height="7" rx="1" />
            <rect x="10" y="5.5" width="4" height="12.5" rx="1" />
            <rect x="15.5" y="14" width="4" height="4" rx="1" />
        </>
    ),
    // Etiqueta de precio: la forma con la que se rotula una oferta en la gondola.
    promociones: (
        <>
            <path d="M12.2 3H20a1 1 0 0 1 1 1v7.8a1 1 0 0 1-.29.7l-8.2 8.2a1 1 0 0 1-1.42 0l-7.8-7.8a1 1 0 0 1 0-1.42l8.2-8.2a1 1 0 0 1 .71-.28Z" />
            <circle cx="16.5" cy="7.5" r="1.5" />
        </>
    ),
    usuarios: (
        <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4.5 20.5v-1a5.5 5.5 0 0 1 5.5-5.5h4a5.5 5.5 0 0 1 5.5 5.5v1" />
        </>
    ),
    configuracion: (
        <>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.1 14.6a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1.05-1.46 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.46-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 .97-1.47V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 .97 1.46 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47.97H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.46.97Z" />
        </>
    ),
    sol: (
        <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8" />
        </>
    ),
    luna: <path d="M20.5 13.5A8.5 8.5 0 1 1 10.5 3.5a6.8 6.8 0 0 0 10 10Z" />,
    salir: (
        <>
            <path d="M9.5 21H5.5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="m16 16.5 4.5-4.5L16 7.5" />
            <path d="M20.5 12h-11" />
        </>
    ),
    menu: <path d="M4 6.5h16M4 12h16M4 17.5h16" />,
    plegar: <path d="m14.5 18-6-6 6-6" />,
    desplegar: <path d="m9.5 18 6-6-6-6" />,
}

interface Props {
    nombre: NombreIcono
    /** Tamanio en pixeles. Por defecto 20, que es lo que usa la navegacion. */
    tamanio?: number
    className?: string
}

export function Icono({ nombre, tamanio = 20, className }: Props) {
    return (
        <svg
            className={className}
            width={tamanio}
            height={tamanio}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            // Decorativo: el texto al lado ya dice que es, y cuando no lo hay
            // el boton lleva aria-label.
            aria-hidden="true"
            focusable="false"
        >
            {TRAZOS[nombre]}
        </svg>
    )
}
